/**
 * PD-17390 — Compliance > Sent to ERP: the vocabulary of the license export
 * log (`license_export_log`, LicenseExportLogEntity), shared by its list
 * endpoint (crm-api), its filter tray (libs/core) and the page.
 *
 * Rows are written by mover-integration's client license export, one per
 * license per run. `result` is the ERP's own answer (Blue Link's six words,
 * kept as Blue Link returns them) or Workd's word for what happened.
 */

/** How a result reads on the page: what kind of thing happened. */
export type LicenseExportResultTone = 'info' | 'neutral' | 'warning' | 'danger';

export interface LicenseExportResultMetaModel {
	/** The stored `result` value, and the filter option id. */
	id: string;
	label: string;
	tone: LicenseExportResultTone;
	needsAcknowledgement: boolean;
}

/**
 * Every result the integration writes, in the order the Result filter offers
 * them.
 *
 * `needsAcknowledgement` is THE rule (approved for PD-17390): a row needs
 * acknowledging when the ERP's data changed (Blue Link updated or created the
 * license; NetSuite or DOit took a license that changed in Workd) or when the
 * push did not go through. "No Change", "Kept ERP Date", "Resent" and skips
 * are listed but need nothing from anyone.
 *
 * mover-integration cannot import this library, so it keeps a copy of the
 * rule (`ClientLicenseExportLogService.RESULTS_NEEDING_ACKNOWLEDGEMENT`) and
 * stores the answer on each row as it writes it, so a row's flag never
 * changes after the fact. Change both together; each repository's test pins
 * the same set.
 */
export const LICENSE_EXPORT_RESULTS: ReadonlyArray<LicenseExportResultMetaModel> =
	[
		// The ERP's data changed.
		{ id: 'Updated', label: 'Updated', tone: 'info', needsAcknowledgement: true },
		{ id: 'Created', label: 'Created', tone: 'info', needsAcknowledgement: true },
		{ id: 'Sent', label: 'Sent', tone: 'info', needsAcknowledgement: true },
		// The push did not go through.
		{
			id: 'Not Found',
			label: 'Not Found',
			tone: 'danger',
			needsAcknowledgement: true,
		},
		{
			id: 'Already Exists',
			label: 'Already Exists',
			tone: 'danger',
			needsAcknowledgement: true,
		},
		{ id: 'Error', label: 'Error', tone: 'danger', needsAcknowledgement: true },
		{
			id: 'Refused',
			label: 'Refused',
			tone: 'danger',
			needsAcknowledgement: true,
		},
		{ id: 'Failed', label: 'Failed', tone: 'danger', needsAcknowledgement: true },
		// Nothing for anyone to do.
		{
			id: 'No Change',
			label: 'No Change',
			tone: 'neutral',
			needsAcknowledgement: false,
		},
		{
			id: 'Kept ERP Date',
			label: "Kept ERP's Later Date",
			tone: 'neutral',
			needsAcknowledgement: false,
		},
		{ id: 'Resent', label: 'Resent', tone: 'neutral', needsAcknowledgement: false },
		{
			id: 'Skipped',
			label: 'Skipped',
			tone: 'warning',
			needsAcknowledgement: false,
		},
	];

/** The results that need acknowledging (see LICENSE_EXPORT_RESULTS). */
export const LICENSE_EXPORT_RESULTS_NEEDING_ACKNOWLEDGEMENT: ReadonlyArray<string> =
	LICENSE_EXPORT_RESULTS.filter((result) => result.needsAcknowledgement).map(
		(result) => result.id,
	);

/**
 * Whether a result needs acknowledging. A result this list does not know (a
 * newer integration's word) needs it: an unknown answer is more likely a
 * problem than not, and acknowledging it costs one click.
 */
export function licenseExportResultNeedsAcknowledgement(
	result: string | null | undefined,
): boolean {
	return licenseExportResultMeta(result).needsAcknowledgement;
}

/** A result's label and tone; an unknown result keeps its own word. */
export function licenseExportResultMeta(
	result: string | null | undefined,
): LicenseExportResultMetaModel {
	const value = (result ?? '').trim();
	const known = LICENSE_EXPORT_RESULTS.find((entry) => entry.id === value);
	if (known != null) {
		return known;
	}
	return {
		id: value,
		label: value === '' ? 'Unknown' : value,
		tone: 'warning',
		needsAcknowledgement: true,
	};
}

/** The ERPs the license export writes to, as `erpName` stores them. */
export const LICENSE_EXPORT_ERPS = [
	{ id: 'Blue Link', label: 'Blue Link' },
	{ id: 'NetSuite', label: 'NetSuite' },
	{ id: 'DOit', label: 'DOit' },
] as const satisfies ReadonlyArray<{ id: string; label: string }>;

/**
 * Where a row stands with the person reviewing the page. Option ids are not
 * numeric, so they survive the list's URL state.
 */
export const LICENSE_EXPORT_LOG_STATUSES = [
	{ id: 'needs_acknowledgement', label: 'Needs acknowledgement' },
	{ id: 'acknowledged', label: 'Acknowledged' },
	{ id: 'no_action', label: 'No action needed' },
] as const satisfies ReadonlyArray<{ id: string; label: string }>;

export type LicenseExportLogStatusId =
	(typeof LICENSE_EXPORT_LOG_STATUSES)[number]['id'];

/** A row's status, from what the integration decided and what a person did. */
export function licenseExportLogStatusOf(row: {
	needsAcknowledgement: boolean;
	acknowledgedAt: Date | string | null | undefined;
}): LicenseExportLogStatusId {
	if (row.acknowledgedAt != null) {
		return 'acknowledged';
	}
	return row.needsAcknowledgement ? 'needs_acknowledgement' : 'no_action';
}

/** The most rows one acknowledge or undo request takes. */
export const LICENSE_EXPORT_LOG_MAX_ACKNOWLEDGE = 1000;

/** The longest acknowledgement note kept. */
export const LICENSE_EXPORT_LOG_NOTE_MAX_LENGTH = 2000;
